import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi } from '../personnelApi';
import type { PersonnelRecognition } from '../personnelTypes';
import { sampleRecognition } from '../utils/sampleRecognition';

/**
 * Records or corrects one recognition — CS Form 212, section VIII, item 32.
 *
 * **Captured from the hospital system's own Recognition form**, kept to its four fields. Unlike
 * the Special skills form one entry above, this one does NOT widen the section: item 32 asks what
 * the award was and who gave it, and those are already the columns.
 *
 * **Only the title is required.** An award somebody remembers receiving without remembering which
 * body conferred it, or in which year, is a real state of a 201 file — and a form that refused it
 * would make the record unable to say what is true.
 *
 * **A second award of the same name is a SECOND ROW.** Employee of the Year in 2019 and again in
 * 2023 are two awards, so nothing here refuses a duplicate title — which is the opposite of the
 * skills form, where a second row of one name is one fact recorded twice.
 */
export function EditRecognitionModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelRecognition | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(editing?.title ?? '');
  const [awardedBy, setAwardedBy] = useState(editing?.awardedBy ?? '');
  const [dateAwarded, setDateAwarded] = useState(editing?.dateAwarded ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const today = todayCalendarDate();

  const valid = title.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  /** Fills every required field, so the primary action enables on the first try. */
  function generate() {
    const sample = sampleRecognition();

    setTitle(sample.title);
    setAwardedBy(sample.awardedBy);
    setDateAwarded(sample.dateAwarded);
    setDescription(sample.description);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      title: title.trim(),

      // Empty is ABSENT, not a blank value — one meaning for the null downstream.
      awardedBy: awardedBy.trim() || null,
      dateAwarded: dateAwarded || null,
      description: description.trim() || null,
    };

    try {
      if (editing) await personnelApi.updateRecognition(personnelId, editing.id, request);
      else await personnelApi.addRecognition(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);

      /*
        Every server field name kebabs straight onto an input id here — `dateAwarded` becomes
        `recognition-date-awarded` — so no `fieldIds` map is needed. There is no `<RadioGroup>` on
        this form, which is the one control that needs one.
      */
      focusValidationError(failure, { idPrefix: 'recognition' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit recognition' : 'Add a recognition'}
      size="2xl"
      id="recognition-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'recognition-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="recognition-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="recognition-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          FULL WIDTH, outside the grid — this is the field that NAMES the record, and an award's
          name runs long ("Outstanding Public Service Award, Regional Level"), so a half row is
          the box that truncates while the two facts ABOUT the award need none of that width.
          The shape `<EditEligibilityModal>` already has: the identity leads at full width, the
          facts pair in the grid under it.

          A SIBLING of the grid, never `sm:col-span-2` — both render identically, because a modal
          body and a field grid both space at `gap-4`, and one idiom is what stops the next
          full-width field inventing a second. Not the REMARKS rule, which is about a free note
          under the form; that one is the Citation at the bottom of this file.
        */}
        <TextField
          id="recognition-title"
          label="Recognition"
          required
          description="The award or distinction, as it was conferred — CS Form 212 prints blank lines here. A second award of the same name is a second row, not a correction of the first."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={title.trim() ? error?.fieldError('title') : 'Enter the award or distinction.'}
          disabled={saving}
        />

        {/*
          ONE grid for the two facts ABOUT the award — who conferred it, and when. They read as a
          pair, neither needs the width the title does, and two fields fill the row exactly, so
          neither is stranded.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="recognition-awarded-by"
            label="Awarded by"
            /*
              Optional, and the description says why rather than leaving a required-looking field
              blank: an award somebody remembers without remembering who conferred it is a real
              answer, and a form that refused it would lose the award altogether.
            */
            description="The body that conferred it. Leave blank if nobody recorded it."
            value={awardedBy}
            onChange={(e) => setAwardedBy(e.target.value)}
            error={error?.fieldError('awardedBy')}
            disabled={saving}
          />

          {/*
            ONE column, never spanned — `mm/dd/yyyy` plus the calendar button is a fixed, small
            amount of content, so a date stretched across both columns is a box mostly full of
            nothing beside a field that needed the room.

            `max={today}`: a recognition is something RECEIVED, so the future is a keying error.
            The bound reaches the typed input AND the calendar grid, so a date the server refuses
            is not left clickable in the picker beside it.
          */}
          <DateField
            id="recognition-date-awarded"
            label="Date awarded"
            max={today}
            description="The day it was awarded. Leave blank if nobody wrote it down — undated rows sort last, because an unrecorded date is not a recent one."
            value={dateAwarded}
            onChange={setDateAwarded}
            error={error?.fieldError('dateAwarded')}
            disabled={saving}
          />
        </div>

        {/*
          FULL WIDTH, outside the grid — the shape every free-note field in the product has. It is
          the longest value on the form and the one most likely to be a sentence, so half a row is
          the box that truncates while the field beside it needed none of the room.

          Plain text and NOT a rich-text field: it is a sentence a table renders in a cell, which
          is exactly the case the rich-text rule refuses. An editor here would buy a write-time
          sanitizer, a widened column and 450 kB of JavaScript to store one line.
        */}
        <TextField
          id="recognition-description"
          label="Citation"
          description="What it was given for."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
